import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ContactInboxList — shared contact-candidate review queue (TekStak + GAS).
 *
 * The Contact Radar harvester stages Gmail-derived candidates; nothing enters
 * the CRM without an approval here. This component is the ONE implementation
 * of the review semantics (audit CRM-3 — the two bespoke inboxes drifted into
 * giving the same control opposite meanings):
 *
 * - Names are EDITABLE, prefilled with stored values or the server's
 *   convention-derived suggestions (never mint contacts named "jsmith").
 * - Approve is ALWAYS enabled — quick capture; a contact may enter the CRM
 *   with no account at all.
 * - The per-row account picker adds COVERAGE links (which accounts this
 *   person is relevant to — zero to many). It never sets the home org.
 * - Home org comes ONLY from the domain Teach row, which maps the whole
 *   domain (now and future) to an account — existing or created on the spot.
 * - Bulk approve carries whatever the row inputs SHOW (typed edits and
 *   suggested prefills alike) — the visible value is the promised value
 *   (audit CRM-2).
 * - Reject is the harvest suppression list; the rejected view restores.
 * - Rows carry a SIGNAL TIER computed server-side (enriched > two_way >
 *   outbound > named > thin). Best first, filterable by tier, and the thin
 *   tier — inbound-only, partial name, no phone/title — can be cleared in
 *   one (undoable) action. Without this the queue is mostly one-way senders
 *   with a bare local part, and the rows worth reviewing are buried.
 *
 * Presentational: candidates/accounts in via props (raw contact_candidates
 * API rows — snake_case, both backends share services/crm/contactCandidates
 * so the shapes match), persistence out via callbacks. Hosts own transport
 * (GAS: callServer cx*; TekStak: apiClient workspace routes) and toasts.
 *
 * Styles: import '@techstack/ui/dist/styles/contact-inbox.css' once per app.
 */
import { useState, useRef, useEffect } from 'react';
import { formatDate } from '@techstack/core';
/** Column name → what a person calls it, for the "approve fills …" line. */
const FIELD_LABELS = {
    email: 'email',
    phone: 'phone',
    title: 'title',
    linkedin_url: 'LinkedIn',
    org_unit: 'unit',
};
const fieldLabel = (f) => FIELD_LABELS[f] || f;
export const SIGNAL_TIER_ORDER = ['enriched', 'two_way', 'outbound', 'named', 'thin'];
const TIER_LABEL = {
    enriched: 'Phone / title',
    two_way: 'Two-way',
    outbound: 'I emailed',
    named: 'Full name',
    thin: 'Name only',
};
const TIER_HINT = {
    enriched: 'Carries a phone number or a title — the rows worth the org chart.',
    two_way: 'Mail went both directions: a real correspondent.',
    outbound: 'You wrote to them, no reply captured yet.',
    named: 'Inbound only, but a full first + last name is known.',
    thin: 'Inbound only, partial name, no phone or title — mostly noise.',
};
function candidateName(c) {
    return (`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email.split('@')[0]);
}
function fmtDay(iso) {
    // X-16: shared formatter (core) — 'short' == { month:'short', day:'numeric' }.
    return formatDate(iso, 'short') || null;
}
// ── Account combobox (search-as-you-type, keyboard-first) ─────────────────
// Ported from the GAS AccountPicker after audit X-2 (the old blur-timeout
// pattern made keyboard use impossible). onSelect receives {id, label} or
// {isNew: true, name} when allowCreate.
function accountScore(query, a) {
    const s = query.toLowerCase();
    const fields = [a.acronym, a.common_name || a.name, a.name]
        .filter(Boolean)
        .map((f) => String(f).toLowerCase());
    let best = 0;
    fields.forEach((f, idx) => {
        let score = 0;
        if (f === s)
            score = 100;
        else if (f.startsWith(s))
            score = 80;
        else if (f.split(/\s+/).some((w) => w.startsWith(s)))
            score = 60;
        else if (f.includes(s))
            score = 40;
        else {
            let i = 0;
            for (const ch of f) {
                if (ch === s[i])
                    i++;
                if (i === s.length)
                    break;
            }
            if (i === s.length)
                score = 20;
        }
        if (score > 0 && idx === 0)
            score += 5; // acronym matches edge out name matches
        if (score > best)
            best = score;
    });
    return best;
}
function AccountCombobox({ accounts, onSelect, busy, allowCreate = true, placeholder, compact = false, }) {
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [hi, setHi] = useState(-1);
    const rootRef = useRef(null);
    const query = q.trim();
    const matches = query.length >= 1
        ? accounts
            .map((a) => ({ a, s: accountScore(query, a) }))
            .filter((x) => x.s > 0)
            .sort((x, y) => y.s - x.s)
            .slice(0, 8)
        : [];
    const options = matches.map(({ a }) => ({ kind: 'account', a }));
    if (allowCreate && query)
        options.push({ kind: 'create' });
    const choose = (opt) => {
        if (!opt)
            return;
        if (opt.kind === 'create')
            onSelect({ isNew: true, name: query });
        else
            onSelect({ id: opt.a.id, label: opt.a.acronym || opt.a.common_name || opt.a.name });
        setOpen(false);
        setQ('');
        setHi(-1);
    };
    useEffect(() => {
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);
    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHi((h) => Math.min(h + 1, options.length - 1));
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHi((h) => Math.max(h - 1, 0));
        }
        else if (e.key === 'Enter') {
            if (open && options.length > 0) {
                e.preventDefault();
                choose(options[hi >= 0 ? hi : 0]);
            }
        }
        else if (e.key === 'Escape') {
            setOpen(false);
            setHi(-1);
        }
    };
    return (_jsxs("div", { ref: rootRef, className: `cxi-picker ${compact ? 'cxi-picker-compact' : ''}`, children: [_jsx("input", { value: q, placeholder: placeholder, disabled: busy, role: "combobox", "aria-expanded": open, "aria-autocomplete": "list", onFocus: () => {
                    setOpen(true);
                    setHi(-1);
                }, onChange: (e) => {
                    setQ(e.target.value);
                    setHi(-1);
                }, onKeyDown: onKeyDown }), open && query && (_jsx("div", { role: "listbox", className: "cxi-picker-list", children: options.map((opt, i) => opt.kind === 'account' ? (_jsxs("div", { role: "option", "aria-selected": i === hi, className: `cxi-picker-opt ${i === hi ? 'cxi-picker-hi' : ''}`, onMouseEnter: () => setHi(i), onMouseDown: () => choose(opt), children: [opt.a.acronym ? _jsx("b", { children: opt.a.acronym }) : null, opt.a.acronym ? ' — ' : '', opt.a.common_name || opt.a.name, opt.a.org_type && opt.a.org_type !== 'customer' ? (_jsxs("span", { className: "cxi-dim", children: [" (", opt.a.org_type, ")"] })) : null] }, opt.a.id)) : (_jsxs("div", { role: "option", "aria-selected": i === hi, className: `cxi-picker-opt cxi-picker-create ${i === hi ? 'cxi-picker-hi' : ''}`, onMouseEnter: () => setHi(i), onMouseDown: () => choose(opt), children: ["\u2795 Create new account \u201C", query, "\u201D"] }, "__create__"))) }))] }));
}
// ── Candidate row ──────────────────────────────────────────────────────────
function CandidateRow({ c, accounts, busy, onApprove, onReject, onEdit, }) {
    const [first, setFirst] = useState(c.first_name || c.suggested_first_name || '');
    const [last, setLast] = useState(c.last_name || c.suggested_last_name || '');
    const [links, setLinks] = useState([]);
    const isSuggested = (!c.first_name || !c.last_name) && (c.suggested_first_name || c.suggested_last_name);
    const seen = fmtDay(c.last_seen_at);
    // Report current edits upward so "Approve all N" carries them instead of
    // silently discarding what the row UI invited the user to type (CRM-2).
    useEffect(() => {
        onEdit(c.id, {
            first_name: first,
            last_name: last,
            coverage_account_ids: links.map((x) => x.id),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [first, last, links]);
    return (_jsxs("div", { className: "cxi-row", children: [_jsxs("div", { className: "cxi-row-main", children: [_jsxs("div", { className: "cxi-names", children: [_jsx("input", { className: "cxi-input cxi-input-first", value: first, placeholder: "First", "aria-label": "First name", disabled: busy, onChange: (e) => setFirst(e.target.value) }), _jsx("input", { className: "cxi-input cxi-input-last", value: last, placeholder: "Last", "aria-label": "Last name", disabled: busy, onChange: (e) => setLast(e.target.value) }), c.confidence === 'high' && _jsx("span", { className: "cxi-chip cxi-chip-high", children: "high" }), isSuggested && (_jsx("span", { className: "cxi-dim", title: `Suggested from this domain's email naming convention${c.domain_convention ? ` (${c.domain_convention}@)` : ''}`, children: "suggested" }))] }), _jsxs("div", { className: "cxi-meta", children: [c.email, " \u00B7 \u2191", c.sent_count || 0, " \u2193", c.received_count || 0, c.calendar_count ? ` · ${c.calendar_count} meeting${c.calendar_count === 1 ? '' : 's'}` : '', seen ? ` · last ${seen}` : ''] }), (c.title || c.phone || c.department) && (
                    // Signature-parsed detail (in-tenant, deterministic). Read-only
                    // here: it rides along on approve, and the contact record is the
                    // place to correct it.
                    _jsx("div", { className: "cxi-meta cxi-meta-rich", children: [c.title, c.department, c.phone].filter(Boolean).join(' · ') })), c.matched_contact_id && (
                    // Already someone in the CRM. Approving lands these facts on that
                    // person — usually an org-chart card carried in from LinkedIn with
                    // no address — instead of minting a second record for one human.
                    _jsxs("div", { className: "cxi-match", title: c.matched_by === 'email'
                            ? 'Matched on their email address'
                            : 'Matched on the same full name inside this account', children: [_jsx("span", { className: "cxi-chip cxi-chip-match", children: "already in CRM" }), _jsxs("span", { children: [c.matched_contact_name || 'this contact', c.matched_contact_account ? ` · ${c.matched_contact_account}` : '', c.matched_contact_benched ? ' · on the bench' : ''] }), _jsx("span", { className: "cxi-dim", children: c.would_fill && c.would_fill.length
                                    ? `approve fills ${c.would_fill.map(fieldLabel).join(', ')}`
                                    : 'approve adds nothing new' })] })), _jsxs("div", { className: "cxi-links", children: [links.map((x) => (_jsxs("button", { type: "button", className: "cxi-chip cxi-chip-link", title: `Remove coverage link to ${x.label}`, onClick: () => setLinks((ls) => ls.filter((y) => y.id !== x.id)), children: [x.label, " \u2715"] }, x.id))), _jsx(AccountCombobox, { accounts: accounts, busy: busy, allowCreate: false, compact: true, placeholder: links.length ? '+ another account…' : '+ link to accounts (optional)…', onSelect: (v) => {
                                    if ('id' in v)
                                        setLinks((ls) => (ls.some((y) => y.id === v.id) ? ls : [...ls, v]));
                                } })] })] }), _jsxs("div", { className: "cxi-row-actions", children: [_jsx("button", { className: "cxi-btn cxi-btn-approve", disabled: busy, title: c.matched_contact_id
                            ? `Add what this row knows to ${c.matched_contact_name || 'the existing contact'} — no second record`
                            : links.length
                                ? `Approve and link to ${links.length} account${links.length > 1 ? 's' : ''}`
                                : 'Approve into CRM (link accounts later any time)', onClick: () => onApprove(c.id, {
                            first_name: first,
                            last_name: last,
                            coverage_account_ids: links.map((x) => x.id),
                        }), children: "\u2713 Approve" }), _jsx("button", { className: "cxi-btn cxi-btn-reject", disabled: busy, title: "Reject \u2014 suppresses future harvests (undo available after)", onClick: () => onReject(c.id), children: "\u2715" })] })] }));
}
// ── Main component ─────────────────────────────────────────────────────────
export function ContactInboxList({ candidates, accounts, busy = false, rejected, totalPending, tiers, tier = 'all', onTierChange, onApprove, onApproveBulk, onReject, onUnreject, onRejectTier, onTeachDomain, onLoadRejected, emptyText = 'No contacts waiting for review.', }) {
    const [showRejected, setShowRejected] = useState(false);
    // domain → picker choice; domain → org type (Teach row state)
    const [teachSel, setTeachSel] = useState({});
    const [teachType, setTeachType] = useState({});
    // Live per-row edits keyed by candidate id — a ref because rows update it
    // on every keystroke and only bulk approve reads it.
    const rowEdits = useRef({});
    const noteRowEdit = (id, edit) => {
        rowEdits.current[id] = edit;
    };
    const bulkEntryFor = (c) => {
        const e = rowEdits.current[c.id];
        if (!e)
            return c.id;
        const entry = { id: c.id };
        if ((e.first_name || '').trim())
            entry.first_name = (e.first_name || '').trim();
        if (e.last_name !== undefined)
            entry.last_name = (e.last_name || '').trim();
        if (e.coverage_account_ids && e.coverage_account_ids.length)
            entry.coverage_account_ids = e.coverage_account_ids;
        return entry;
    };
    // Matched candidates group by account; unknown ones group by domain.
    const matched = new Map();
    const unknown = new Map();
    for (const c of candidates) {
        if (c.matched_account_id) {
            const key = c.matched_account_name || 'Account';
            if (!matched.has(key))
                matched.set(key, []);
            matched.get(key).push(c);
        }
        else {
            const key = c.domain || c.email.split('@')[1] || 'unknown';
            if (!unknown.has(key))
                unknown.set(key, []);
            unknown.get(key).push(c);
        }
    }
    // Bulk-clear arm: disarms itself after a few seconds so a stray first
    // click can't sit there waiting to become a second one.
    const [armed, setArmed] = useState(false);
    useEffect(() => {
        if (!armed)
            return undefined;
        const t = setTimeout(() => setArmed(false), 4000);
        return () => clearTimeout(t);
    }, [armed]);
    const tierCounts = tiers || {};
    const hasTierCounts = SIGNAL_TIER_ORDER.some((t) => (tierCounts[t] || 0) > 0);
    const allCount = SIGNAL_TIER_ORDER.reduce((n, t) => n + (tierCounts[t] || 0), 0);
    // The bulk clear follows the filter: looking at one weak tier, it clears
    // THAT tier; unfiltered, it offers the thin pile. It never offers to clear
    // `enriched` — those rows are the reason the queue exists.
    const clearTier = tier !== 'all' && tier !== 'enriched' ? tier : 'thin';
    const clearCount = tierCounts[clearTier] || 0;
    return (_jsxs("div", { className: "cxi-root", children: [hasTierCounts && onTierChange && (_jsxs("div", { className: "cxi-tiers", role: "group", "aria-label": "Filter by signal", children: [_jsxs("button", { type: "button", className: `cxi-tier ${tier === 'all' ? 'cxi-tier-on' : ''}`, "aria-pressed": tier === 'all', disabled: busy, onClick: () => onTierChange('all'), children: ["All ", _jsx("span", { className: "cxi-dim", children: allCount })] }), SIGNAL_TIER_ORDER.filter((t) => (tierCounts[t] || 0) > 0).map((t) => (_jsxs("button", { type: "button", className: `cxi-tier cxi-tier-${t} ${tier === t ? 'cxi-tier-on' : ''}`, "aria-pressed": tier === t, title: TIER_HINT[t], disabled: busy, onClick: () => onTierChange(t), children: [TIER_LABEL[t], " ", _jsx("span", { className: "cxi-dim", children: tierCounts[t] })] }, t)))] })), _jsxs("div", { className: "cxi-bar", children: [_jsxs("span", { className: "cxi-count", children: [typeof totalPending === 'number' && totalPending > candidates.length
                                ? `showing ${candidates.length} of ${totalPending} pending`
                                : `${candidates.length} pending`, tier !== 'all' ? ` · ${TIER_LABEL[tier]}` : ''] }), onRejectTier && clearCount > 0 && (
                    // The bulk exit for a queue full of one-way senders. Armed
                    // two-click (no prompts), and the host offers a single Undo —
                    // rejection is the harvest suppression list.
                    _jsx("button", { className: `cxi-btn cxi-btn-reject-bulk ${armed ? 'cxi-btn-armed' : ''}`, disabled: busy, title: armed
                            ? `Click again to reject all ${clearCount}. They stay recoverable from the Rejected view.`
                            : `Reject all ${clearCount} "${TIER_LABEL[clearTier]}" rows — ${TIER_HINT[clearTier]} Undoable.`, onClick: () => {
                            if (armed) {
                                setArmed(false);
                                onRejectTier(clearTier, clearCount);
                            }
                            else {
                                setArmed(true);
                            }
                        }, children: armed
                            ? `Sure? Reject ${clearCount}`
                            : `Clear ${clearCount} ${TIER_LABEL[clearTier].toLowerCase()}` })), onLoadRejected && (_jsx("button", { className: "cxi-btn", disabled: busy, onClick: () => {
                            const next = !showRejected;
                            setShowRejected(next);
                            if (next && rejected == null)
                                onLoadRejected();
                        }, children: showRejected ? 'Hide rejected' : 'Rejected' }))] }), showRejected && (_jsxs("div", { className: "cxi-group", children: [_jsx("div", { className: "cxi-group-head", children: _jsx("span", { className: "cxi-group-title", children: "Rejected (suppressed from harvesting)" }) }), rejected == null && _jsx("div", { className: "cxi-empty", children: "Loading\u2026" }), rejected && rejected.length === 0 && _jsx("div", { className: "cxi-empty", children: "Nothing rejected." }), (rejected || []).map((c) => (_jsxs("div", { className: "cxi-row", children: [_jsxs("div", { className: "cxi-row-main", children: [_jsx("div", { className: "cxi-names", children: candidateName(c) }), _jsx("div", { className: "cxi-meta", children: c.email })] }), _jsx("div", { className: "cxi-row-actions", children: _jsx("button", { className: "cxi-btn", disabled: busy, title: "Restore to the pending queue", onClick: () => onUnreject(c.id), children: "Restore" }) })] }, c.id)))] })), candidates.length === 0 && _jsx("div", { className: "cxi-empty", children: emptyText }), [...matched.entries()].map(([name, group]) => (_jsxs("div", { className: "cxi-group", children: [_jsxs("div", { className: "cxi-group-head", children: [_jsx("span", { className: "cxi-group-title", children: name }), _jsxs("button", { className: "cxi-btn", disabled: busy, onClick: () => onApproveBulk(group.map(bulkEntryFor)), children: ["Approve all ", group.length] })] }), group.map((c) => (_jsx(CandidateRow, { c: c, accounts: accounts, busy: busy, onApprove: onApprove, onReject: onReject, onEdit: noteRowEdit }, c.id)))] }, name))), [...unknown.entries()].map(([domain, group]) => {
                const chosen = teachSel[domain] || null;
                const orgType = teachType[domain] || 'customer';
                return (_jsxs("div", { className: "cxi-group", children: [_jsxs("div", { className: "cxi-group-head cxi-group-teach", children: [_jsxs("span", { className: "cxi-group-title", children: ["@", domain, " ", _jsxs("span", { className: "cxi-dim", children: ["(", group.length, ")"] })] }), chosen && (_jsxs("button", { type: "button", className: "cxi-chip cxi-chip-link", title: "Clear selection", onClick: () => setTeachSel((s) => ({ ...s, [domain]: null })), children: ['isNew' in chosen ? `➕ ${chosen.name} (new)` : chosen.label, " \u2715"] })), !chosen && (_jsx(AccountCombobox, { accounts: accounts, busy: busy, placeholder: "Home org for this domain (who they work for)\u2026", onSelect: (v) => setTeachSel((s) => ({ ...s, [domain]: v })) })), _jsxs("select", { className: "cxi-input", disabled: busy, value: orgType, "aria-label": "Org type", onChange: (e) => setTeachType((t) => ({ ...t, [domain]: e.target.value })), children: [_jsx("option", { value: "customer", children: "customer" }), _jsx("option", { value: "partner", children: "partner" }), _jsx("option", { value: "internal", children: "internal" })] }), _jsx("button", { className: "cxi-btn cxi-btn-approve", disabled: busy || !chosen, title: "Set the domain's home org \u2014 every @domain contact files under it (now and future). This is NOT customer assignment; that's the per-row '+ link to accounts' chips.", onClick: () => {
                                        if (!chosen)
                                            return;
                                        onTeachDomain(domain, 'isNew' in chosen ? { account_name: chosen.name } : { account_id: chosen.id }, orgType);
                                    }, children: "Teach" })] }), group.map((c) => (_jsx(CandidateRow, { c: c, accounts: accounts, busy: busy, onApprove: onApprove, onReject: onReject, onEdit: noteRowEdit }, c.id)))] }, domain));
            })] }));
}
export default ContactInboxList;
